/**
 * IndexedDB persistence for the SPI dataset — the local half of the screen-serving
 * performance work (the backend caches the build; this caches the BYTES on the client).
 *
 * Stores ONE record: the mapped `SpiProductsResult` plus the HTTP `ETag` the backend
 * returned for it. On the next visit the table renders INSTANTLY from this record while a
 * conditional `If-None-Match` request revalidates in the background — a `304` means the
 * cached dataset is still current and nothing is re-downloaded (see `fetchSpiProducts`).
 *
 * localStorage is far too small for the multi-MB dataset, so IndexedDB is the right store.
 * Every call degrades gracefully (returns null / no-ops) when IndexedDB is unavailable
 * (private mode, SSR/tests) so the app always falls back to a normal network fetch.
 */
import { logger } from '../../../utils/logger';

import type { SpiProductsResult } from './spiApiTypes';

const DB_NAME = 'rw-spi';
const STORE = 'dataset';
const RECORD_KEY = 'live';
/**
 * Schema version — bump when the mapped `SpiSku` SHAPE changes so a stale-shape cache is DISCARDED
 * rather than mis-rendered. v2: added `invKnown` (new-product OOS rule) to `SpiSku`.
 * v3: added `containerStageId` to `openPOs[]` (container rows resolve their own stage via metadata).
 * v4: added `vendorId` to `openPOs[]` (raw vendor id for the ERP PO deep-link; `vendor` is the label).
 * v5: force a hard reload to discard datasets whose delta-merged month window went stale (the axis
 * froze on an old month until IndexedDB was cleared); pairs with the stale-window guard in fetch.
 */
const SPI_SCHEMA_VERSION = 5;
/**
 * App build version. Set `REACT_APP_VERSION` in the build (e.g. to the git SHA / CI build number)
 * so EVERY deploy changes it; defaults to 'dev' locally. Folded into {@link CACHE_VERSION} below so
 * shipping new code automatically invalidates ("hard-refreshes") a stale local cache written by the
 * previous build — the data structure or calc may have changed even when the SPI_SCHEMA_VERSION didn't.
 */
const APP_VERSION = process.env.REACT_APP_VERSION ?? 'dev';
/**
 * The cache key the record is stamped with. A read whose stored version differs — a SPI_SCHEMA_VERSION
 * bump OR a new app build — is treated as stale: it's ignored, and the next fetch (a full 200,
 * since no If-None-Match is sent) overwrites it with a fresh, current-version record.
 */
const CACHE_VERSION = `${SPI_SCHEMA_VERSION}|${APP_VERSION}`;

export interface CachedDataset {
  /** ETag of the cached payload, replayed as `If-None-Match` on the next fetch. */
  etag: string | null;
  result: SpiProductsResult;
  /** Max per-SKU lastUpdatedAt in `result` — the watermark sent to `/products/delta`. */
  watermark: number | null;
}

interface StoredRecord extends CachedDataset {
  /** Combined schema + app-build version the record was written under. */
  version: string;
}

/** Open (and lazily create) the object store. Resolves null when IndexedDB is unavailable. */
function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, 1);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
}

/** Read the cached dataset, or null when absent / schema-stale / unavailable. */
export async function getCachedDataset(): Promise<CachedDataset | null> {
  try {
    const db = await openDb();
    if (!db) {
      return null;
    }
    const rec = await new Promise<StoredRecord | undefined>((resolve) => {
      const tx = db.transaction(STORE, 'readonly');
      const get = tx.objectStore(STORE).get(RECORD_KEY);
      get.onsuccess = () => resolve(get.result as StoredRecord | undefined);
      get.onerror = () => resolve(undefined);
    });
    db.close();
    // Discard a record from an older schema OR an older app build — it may be shaped or computed
    // differently from what the current code expects. Ignoring it makes the next fetch a full 200
    // that overwrites it with a fresh, current-version record.
    if (!rec || rec.version !== CACHE_VERSION || !rec.result) {
      return null;
    }
    return { etag: rec.etag ?? null, result: rec.result, watermark: rec.watermark ?? null };
  } catch (error) {
    logger.warn('[SPI] dataset cache read failed; falling back to network', error);
    return null;
  }
}

/** Write-through the freshly fetched dataset + its ETag + delta watermark. Best-effort; never throws. */
export async function putCachedDataset(
  result: SpiProductsResult,
  etag: string | null,
  watermark: number | null,
): Promise<void> {
  try {
    const db = await openDb();
    if (!db) {
      return;
    }
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      const record: StoredRecord = { version: CACHE_VERSION, etag, result, watermark };
      tx.objectStore(STORE).put(record, RECORD_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
    db.close();
  } catch (error) {
    logger.warn('[SPI] dataset cache write failed (non-fatal)', error);
  }
}

/**
 * Delete the cached dataset. Called on LOGOUT so a signed-out user — or a different user who signs
 * in next on the same browser — never sees the previous session's cached business data. IndexedDB
 * is per-origin and shared across tabs, so one clear wipes it for every tab. Best-effort; never throws.
 */
export async function clearCachedDataset(): Promise<void> {
  try {
    const db = await openDb();
    if (!db) {
      return;
    }
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(RECORD_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
    db.close();
  } catch (error) {
    logger.warn('[SPI] dataset cache clear failed (non-fatal)', error);
  }
}
